import { Component, OnDestroy, OnInit } from "@angular/core";
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { Observable, Subscription } from "rxjs";
import { ActivatedRoute, Router } from "@angular/router";
import { Customer } from "../../shared/models/customer";
import { DataStoreService } from "../../shared/services/api/data-store.service";
import { NotificationsService } from "angular2-notifications";
import { CustomValidators } from "../../shared/services/CustomValidators";
import { Address } from "../../shared/models/address";
import { Site } from "../../shared/models/site";
import { CustomerCategory } from "../../shared/models/customer-category";
import { CustomerComponent } from "../customer.component";
import { ErrorMessageService } from "../../shared/services/error-message.service";


export interface AllValidationErrors {
  control_name: string;
  error_name: string;
  error_value: any;
}

export interface FormGroupControls {
  [key: string]: AbstractControl;
}

export function getFormValidationErrors(controls: FormGroupControls): AllValidationErrors[] {
  let errors: AllValidationErrors[] = [];
  Object.keys(controls).forEach(key => {
    const control = controls[key];
    if (control instanceof FormGroup) {
      errors = errors.concat(getFormValidationErrors(control.controls));
    }
    const controlErrors: ValidationErrors = controls[key].errors;
    if (controlErrors !== null) {
      Object.keys(controlErrors).forEach(keyError => {
        errors.push({
          control_name: key,
          error_name: keyError,
          error_value: controlErrors[keyError]
        });
      });
    }
  });
  return errors;
}

@Component({
  selector: 'app-customer-form',
  templateUrl: './customer-form.component.html',
  styleUrls: ['./customer-form.component.css']
})
export class CustomerFormComponent implements OnInit, OnDestroy {


  private routeSubscription: Subscription;
  private customer: Customer = null;
  form: FormGroup;
  loading = false;
  private sitesToDelete: Site[] = [];
  public isNew: boolean;

  categories: CustomerCategory[] = [];


  constructor(private formBuilder: FormBuilder,
              private route: ActivatedRoute,
              private dataStore: DataStoreService,
              private notificationService: NotificationsService,
              private errorMessageService: ErrorMessageService,
              private router: Router) {
  }

  ngOnInit() {
    this.createDefaultCustomer();
    this.createForm(this.customer);
    this.routeSubscription = this.route.data.subscribe((data: { customer: Customer }) => {
      if (data.customer != null) {
        this.customer = data.customer;
      }
      this.isNew = this.customer.id == null;

    }, (error) => console.error(error)); // TODO proper error handling

    this.dataStore.findAll(CustomerCategory).map(doc => doc.getModels()).subscribe(categories => {
      this.categories.push(...categories);
      this.createForm(this.customer);
    });
  }


  private createDefaultCustomer() {
    this.customer = this.dataStore.createRecord(Customer);
    let defaultSite = this.dataStore.createRecord(Site);
    defaultSite.address = this.dataStore.createRecord(Address);
    this.customer.sites.push(defaultSite);
  }

  ngOnDestroy(): void {
    this.routeSubscription.unsubscribe();
  }

  private createOpeningHoursForm(): FormArray {
    let openingHourControls: Array<FormGroup> = [];
    for (let i: number = 1; i <= 7; i++) {
      openingHourControls.push(this.formBuilder.group({
        day: [i],
        beginTime: ["", CustomValidators.time],
        endTime: ["", CustomValidators.time],
        breakTime: ["", CustomValidators.time],
        breakDurationMinutes: ["", CustomValidators.number]
      }))
    }
    return this.formBuilder.array(openingHourControls);
  }

  private createForm(customer: Customer) {
    this.form = this.formBuilder.group({
      name: [customer.name, Validators.required],
      sites: this.formBuilder.array(customer.sites.map(site => this.createSiteForm(site)))
    })
  }

  private createSiteForm(site: Site) {
    let emailRegex = '^[a-z0-9]+(\.[_a-z0-9]+)*@[a-z0-9-]+(\.[a-z0-9-]+)*(\.[a-z]{2,15})$';
    return this.formBuilder.group({
      object: [site],
      name: [site.name],
      id: [site.id],
      email: [site.email, Validators.pattern(emailRegex)],
      telephone: [site.telephone],
      category: [CustomerFormComponent.getActualCategoryObject(site.category, this.categories), CustomValidators.oneOf(this.categories)],
      address: this.formBuilder.group({
        street: [site.address.street, Validators.required],
        city: [site.address.city, Validators.required],
        zip: [site.address.zip, Validators.compose([Validators.minLength(4), Validators.required])],
      }),
    })
  }

  private static getActualCategoryObject(category: CustomerCategory, categories: CustomerCategory[]) {
    return category != null ? categories.find(x => category.id == x.id) : null;
  }

  private removeSite(formGroup: FormGroup, index: number) {
    this.sitesToDelete.push(formGroup.value["object"]);
    (this.form.controls["sites"] as FormArray).removeAt(index);
  }

  addSite() {
    let site = this.dataStore.createRecord(Site);
    site.address = this.dataStore.createRecord(Address);
    (this.form.controls["sites"] as FormArray).push(this.createSiteForm(site));
    this.customer.sites.push(site);
  }

  public submit() {
    this.form.updateValueAndValidity();
    if (this.sitesToDelete.length > 0) {
      let deleteObservables = this.sitesToDelete.filter(site => typeof site.id !== "undefined" && site.id != "")
        .map(site => this.dataStore.deleteRecord(Site, site.id));
      Observable.merge(...deleteObservables).subscribe(null, null, () => this.sitesToDelete = []);
    }
    if (this.form.valid && this.form.dirty) {
      Object.assign(this.customer, this.form.value);
      this.loading = true;
      this.customer.save().mergeMap(customer => {
        let siteObservable = (this.form.controls["sites"] as FormArray).controls
          .filter(control => control.dirty)
          .map(control => {
            let site = control.value.object as Site;
            delete control.value.object;
            site.customer = customer;
            Object.assign(site, control.value);
            if (control.value.name == "") {
              site.name = site.address.city;
            }
            return site.save();
          });
        return Observable.merge(...siteObservable);
      }).subscribe(null, (error) => {
        this.errorMessageService.notify(error);
        this.loading = false;
      }, () => {
        this.loading = false;
        if (this.isNew) {
          this.notificationService.success("Kunde angelegt", "es wurde ein neuer Kunde angelegt");
        } else {
          this.notificationService.success("Daten gespeichert", "Kunde wurde gespeichert");
        }
        this.form.markAsPristine();
      });
    }
  }


  displayCategory(category: CustomerCategory) {
    return (category != null) ? category.name : "";
  }

  deleteCustomer() {
    this.dataStore.deleteRecord(Customer, this.customer.id).subscribe(response => {
      if (typeof CustomerComponent.cachedCustomers != "undefined") {
        let index = CustomerComponent.cachedCustomers.indexOf(this.customer);
        CustomerComponent.cachedCustomers.splice(index, 1);
      }
      this.router.navigate(["/customer"]);
    })
  }

  navigateToCustomer() {
    this.router.navigate(["../"], { relativeTo: this.route })
  }

  castToFormGroup(formControl: AbstractControl): FormGroup {
    return formControl as FormGroup;
  }

  castToFormArray(formControl: AbstractControl): FormArray {
    return formControl as FormArray;
  }

}



// WEBPACK FOOTER //
// /home/markush/Projects/fieldsale-wingman-client/src/app/customer/customer-form/customer-form.component.ts